import { Alert, Button } from '@ksyos/design-system';
import { every, isNull, values } from 'lodash';
import type { IKlachten } from '../../../../../../common/validation/customControls/pulmonology';
import type { ValidationErrors } from '../../../../../utils/validation';
import {
    pulmoKlachtenCustomControlDataSchema,
    rookgedragOpties,
} from '../../../../../../common/validation/customControls/pulmonology';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../../../utils/validation';
import { Component } from '../../../../elements/Component';
import { FormContextProvider } from '../../../../elements/formContext';
import { Modal } from '../../../../elements/modals/Modal';
import { NullableTextField } from '../../../../fields/NullableText';
import { RadioGroupField } from '../../../../fields/RadioGroup';
import { TriStateWithTextField } from '../../../../fields/TriStateWithText';
import { AnamneseType } from './anamneseType';
import { AnamneseWrapper } from './AnamneseWrapper';

interface IAnamneseKlachtenProps {
    anamneseData: IKlachten | null;
    deleteAnamneseData: () => Promise<void>;
    initialRookgedrag?: string | null;
    onClose: () => Promise<void>;
    setAnamneseData: (item: IKlachten) => Promise<void>;
}

interface IAnamneseKlachtenState {
    anamneseData: IKlachten;
    errors: ValidationErrors;
    showEmptyValidation: boolean;
}

export class AnamneseKlachten extends Component<IAnamneseKlachtenProps, IAnamneseKlachtenState> {
    constructor(props: IAnamneseKlachtenProps, context?: {}) {
        super(props, context);
        this.state = {
            anamneseData: {
                bekend_met_astma_value: null,
                bekend_met_astma_text: null,
                astma_in_de_familie_value: null,
                astma_in_de_familie_text: null,
                bekend_met_hartfalen_value: null,
                bekend_met_hartfalen_text: null,
                slijm_opgeven_value: null,
                slijm_opgeven_text: null,
                kortademig_s_nachts_value: null,
                kortademig_s_nachts_text: null,
                allergie_value: null,
                allergie_text: null,
                bekend_met_COPD_value: null,
                bekend_met_COPD_text: null,
                hyperreactiviteit_value: null,
                hyperreactiviteit_text: null,
                maagklachten_value: null,
                maagklachten_text: null,
                KNO_klachten_value: null,
                KNO_klachten_text: null,
                rookgedrag: this.props.initialRookgedrag || null,
                beroep: null,
                aantal_exacerbaties_in_het_afgelopen_jaar: null,
                ...(this.props.anamneseData || {}),
            },
            errors: emptyValidationErrors,
            showEmptyValidation: false,
        };
    }

    public render() {
        return (
            <FormContextProvider
                value={{
                    placeholderGroupName: 'request',
                    item: this.state.anamneseData,
                    errors: this.state.errors,
                    onChange: async (key, value) => await this.handleChange(key, value),
                    onDoValidate: async (key) => await this.validateOne(key),
                }}
            >
                <Modal
                    actionButtons={[
                        <Button
                            onClick={async () => await this.props.deleteAnamneseData()}
                            variant="outline"
                        >
                            Verwijder
                        </Button>,
                        <Button
                            onClick={async () => await this.validateAnamneseData()}
                            variant="solid"
                        >
                            Anamnese opslaan
                        </Button>,
                    ]}
                    isOpen
                    onClose={async () => await this.props.onClose()}
                    size="large"
                    title="Klachten anamnese"
                >
                    <AnamneseWrapper anamneseType={AnamneseType.klachten}>
                        <TriStateWithTextField
                            fieldKeyText="bekend_met_astma_text"
                            fieldKeyTriState="bekend_met_astma_value"
                            hasErrors={
                                this.state.errors.has('bekend_met_astma_value') ||
                                this.state.errors.has('bekend_met_astma_text')
                            }
                            label="Bekend met astma"
                            placeholder="Opmerkingen"
                        />
                        <TriStateWithTextField
                            fieldKeyText="astma_in_de_familie_text"
                            fieldKeyTriState="astma_in_de_familie_value"
                            hasErrors={
                                this.state.errors.has('astma_in_de_familie_value') ||
                                this.state.errors.has('astma_in_de_familie_text')
                            }
                            label="Astma in de familie"
                            placeholder="Opmerkingen"
                        />
                        <TriStateWithTextField
                            fieldKeyText="bekend_met_hartfalen_text"
                            fieldKeyTriState="bekend_met_hartfalen_value"
                            hasErrors={
                                this.state.errors.has('bekend_met_hartfalen_value') ||
                                this.state.errors.has('bekend_met_hartfalen_text')
                            }
                            label="Bekend met hartfalen"
                            placeholder="Opmerkingen"
                        />
                        <TriStateWithTextField
                            fieldKeyText="slijm_opgeven_text"
                            fieldKeyTriState="slijm_opgeven_value"
                            hasErrors={
                                this.state.errors.has('slijm_opgeven_value') ||
                                this.state.errors.has('slijm_opgeven_text')
                            }
                            label="Slijm opgeven"
                            placeholder="Opmerkingen"
                        />
                        <TriStateWithTextField
                            fieldKeyText="kortademig_s_nachts_text"
                            fieldKeyTriState="kortademig_s_nachts_value"
                            hasErrors={
                                this.state.errors.has('kortademig_s_nachts_value') ||
                                this.state.errors.has('kortademig_s_nachts_text')
                            }
                            label="Kortademig 's nachts"
                            placeholder="Opmerkingen"
                        />
                        <TriStateWithTextField
                            fieldKeyText="allergie_text"
                            fieldKeyTriState="allergie_value"
                            hasErrors={
                                this.state.errors.has('allergie_value') || this.state.errors.has('allergie_text')
                            }
                            label="Allergie"
                            placeholder="Opmerkingen"
                        />
                        <TriStateWithTextField
                            fieldKeyText="bekend_met_COPD_text"
                            fieldKeyTriState="bekend_met_COPD_value"
                            hasErrors={
                                this.state.errors.has('bekend_met_COPD_value') ||
                                this.state.errors.has('bekend_met_COPD_text')
                            }
                            label="Bekend met COPD"
                            placeholder="Opmerkingen"
                        />
                        <TriStateWithTextField
                            fieldKeyText="hyperreactiviteit_text"
                            fieldKeyTriState="hyperreactiviteit_value"
                            hasErrors={
                                this.state.errors.has('hyperreactiviteit_value') ||
                                this.state.errors.has('hyperreactiviteit_text')
                            }
                            label="Hyperreactiviteit"
                            placeholder="Opmerkingen"
                        />
                        <TriStateWithTextField
                            fieldKeyText="maagklachten_text"
                            fieldKeyTriState="maagklachten_value"
                            hasErrors={
                                this.state.errors.has('maagklachten_value') ||
                                this.state.errors.has('maagklachten_text')
                            }
                            label="Maagklachten"
                            placeholder="Opmerkingen"
                        />
                        <TriStateWithTextField
                            fieldKeyText="KNO_klachten_text"
                            fieldKeyTriState="KNO_klachten_value"
                            hasErrors={
                                this.state.errors.has('KNO_klachten_value') ||
                                this.state.errors.has('KNO_klachten_text')
                            }
                            label="KNO klachten"
                            placeholder="Opmerkingen"
                        />
                        <RadioGroupField
                            fieldKey="rookgedrag"
                            label="Roker"
                            options={rookgedragOpties}
                        />
                        <NullableTextField
                            fieldKey="beroep"
                            label="Beroep"
                            placeholder="Vul hier het beroep in..."
                        />
                        <NullableTextField
                            fieldKey="aantal_exacerbaties_in_het_afgelopen_jaar"
                            label="Exacerbaties in afgelopen jaar"
                            placeholder="Aantal"
                        />

                        {this.state.showEmptyValidation && (
                            <Alert appearance="warning">Je moet iets invullen om het op te kunnen slaan.</Alert>
                        )}
                    </AnamneseWrapper>
                </Modal>
            </FormContextProvider>
        );
    }

    private async handleChange<K extends keyof IKlachten>(name: K, value: IKlachten[K]) {
        await this.setState(({ anamneseData }) => ({ anamneseData: { ...anamneseData, [name]: value } }));
    }

    private async validateOne(name: keyof IKlachten) {
        await this.setState(({ anamneseData, errors }) => ({
            errors: updateValidationErrorsForOne(pulmoKlachtenCustomControlDataSchema, anamneseData, errors, name),
            showEmptyValidation: false,
        }));
    }

    private async validateAnamneseData() {
        if (every(values(this.state.anamneseData), isNull)) {
            await this.setState({ showEmptyValidation: true });

            return;
        }

        const { hasErrors, errors, item } = validate(pulmoKlachtenCustomControlDataSchema, this.state.anamneseData);
        await this.setState({ errors });
        if (!hasErrors) {
            this.props.setAnamneseData(item as IKlachten);
        }
    }
}
